import React, { useState } from 'react';
import { Search, Plus, Trash2, CheckCircle, AlertTriangle, Loader, Globe, FileText, Settings } from 'lucide-react';

export default function App() {
  const [urls, setUrls] = useState(['']);
  const [searchText, setSearchText] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchedContent, setFetchedContent] = useState([]);
  const [apiKey, setApiKey] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [minMatchLength, setMinMatchLength] = useState(5);
  const [caseSensitive, setCaseSensitive] = useState(false);

  const addUrlField = () => {
    setUrls([...urls, '']);
  };

  const removeUrlField = (index) => {
    setUrls(urls.filter((_, i) => i !== index));
  };

  const updateUrl = (index, value) => {
    const newUrls = [...urls];
    newUrls[index] = value;
    setUrls(newUrls);
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Production web scraping using Netlify function
  const scrapeWebContent = async (url) => {
    if (!apiKey.trim()) {
      throw new Error('API key required. Please configure in settings.');
    }

    try {
      const response = await fetch('/.netlify/functions/scrape', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url, apiKey })
      });

      if (!response.ok) {
        throw new Error(`Network error: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error);
      }

      return extractTextFromHtml(data.content);
    } catch (error) {
      console.error('Scraping error:', error);
      throw error;
    }
  };

  // Extract clean text from HTML
  const extractTextFromHtml = (html) => {
    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Remove script and style elements
    const scripts = tempDiv.querySelectorAll('script, style, nav, header, footer, aside');
    scripts.forEach(el => el.remove());

    // Get text content from main content areas
    const contentSelectors = [
      'main', 'article', '.content', '#content', '.post', '.entry',
      '.article-body', '.post-content', 'section', 'div'
    ];

    let extractedText = '';
    for (const selector of contentSelectors) {
      const elements = tempDiv.querySelectorAll(selector);
      for (const element of elements) {
        const text = element.innerText || element.textContent || '';
        if (text.length > extractedText.length) {
          extractedText = text;
        }
      }
    }

    // Fallback to full text if no content areas found
    if (!extractedText.trim()) {
      extractedText = tempDiv.innerText || tempDiv.textContent || '';
    }

    // Clean up the text
    return extractedText
      .replace(/\s+/g, ' ')
      .replace(/\n\s*\n/g, '\n')
      .trim();
  };

  // Enhanced text normalization
  const normalizeText = (text) => {
    let normalized = text.trim();
    
    if (!caseSensitive) {
      normalized = normalized.toLowerCase();
    }

    // Remove extra whitespace and normalize punctuation
    normalized = normalized
      .replace(/[""'']/g, '"')
      .replace(/[–—]/g, '-')
      .replace(/\s+/g, ' ')
      .replace(/\s*([,.!?;:])\s*/g, '$1 ')
      .trim();

    return normalized;
  };

  // Advanced matching algorithm
  const findMatches = (searchText, content, url) => {
    const normalizedSearch = normalizeText(searchText);
    const normalizedContent = normalizeText(content);
    
    if (!normalizedSearch || normalizedSearch.length < 10) return [];
    
    const matches = [];
    const searchWords = normalizedSearch.split(/\s+/);
    const contentWords = normalizedContent.split(/\s+/);
    
    // 1. Exact phrase matching
    if (normalizedContent.includes(normalizedSearch)) {
      matches.push({
        type: 'exact',
        text: searchText,
        url: url,
        confidence: 100,
        severity: 'high',
        startIndex: normalizedContent.indexOf(normalizedSearch)
      });
    }
    
    // 2. Sliding window for consecutive word matches
    for (let windowSize = minMatchLength; windowSize <= searchWords.length; windowSize++) {
      for (let i = 0; i <= searchWords.length - windowSize; i++) {
        const phrase = searchWords.slice(i, i + windowSize).join(' ');
        const phraseIndex = normalizedContent.indexOf(phrase);
        
        if (phraseIndex !== -1) {
          const confidence = Math.round((windowSize / searchWords.length) * 100);
          matches.push({
            type: 'consecutive',
            text: phrase,
            url: url,
            confidence: confidence,
            severity: confidence > 80 ? 'high' : confidence > 60 ? 'medium' : 'low',
            wordCount: windowSize,
            startIndex: phraseIndex
          });
        }
      }
    }
    
    // 3. Fuzzy matching for paraphrased content
    const commonWords = searchWords.filter(word => {
      return word.length > 3 && 
             !['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'].includes(word) &&
             contentWords.includes(word);
    });
    
    if (commonWords.length >= Math.min(5, searchWords.length * 0.4)) {
      const confidence = Math.round((commonWords.length / searchWords.filter(w => w.length > 3).length) * 100);
      matches.push({
        type: 'similarity',
        text: `Keywords: ${commonWords.slice(0, 10).join(', ')}`,
        url: url,
        confidence: confidence,
        severity: confidence > 70 ? 'medium' : 'low',
        wordCount: commonWords.length
      });
    }
    
    // Remove duplicate matches and sort by confidence
    const uniqueMatches = matches.filter((match, index, self) => 
      index === self.findIndex(m => m.type === match.type && m.confidence === match.confidence)
    );
    
    return uniqueMatches.sort((a, b) => b.confidence - a.confidence);
  };

  // Main plagiarism check function
  const checkPlagiarism = async () => {
    if (!searchText.trim()) {
      alert('Please enter text to check');
      return;
    }

    if (searchText.trim().length < 20) {
      alert('Please enter at least 20 characters for accurate detection');
      return;
    }

    const validUrls = urls.filter(url => url.trim() && isValidUrl(url.trim()));
    if (validUrls.length === 0) {
      alert('Please enter at least one valid URL');
      return;
    }

    if (!apiKey.trim()) {
      alert('Please configure your ScrapingBee API key in settings');
      setShowSettings(true);
      return;
    }

    setIsLoading(true);
    setResults([]);
    setFetchedContent([]);
    
    try {
      const contentPromises = validUrls.map(async (url) => {
        try {
          console.log(`Scraping: ${url}`);
          const content = await scrapeWebContent(url.trim());
          return { url, content, error: null, wordCount: content.split(/\s+/).length };
        } catch (error) {
          console.error(`Error scraping ${url}:`, error);
          return { url, content: null, error: error.message };
        }
      });

      const contentResults = await Promise.all(contentPromises);
      setFetchedContent(contentResults);

      // Find matches across all sources
      const allMatches = [];
      contentResults.forEach(({ url, content, error }) => {
        if (content && !error) {
          const matches = findMatches(searchText, content, url);
          allMatches.push(...matches);
        }
      });

      // Sort matches by severity and confidence
      const sortedMatches = allMatches.sort((a, b) => {
        const severityOrder = { high: 3, medium: 2, low: 1 };
        const severityDiff = severityOrder[b.severity] - severityOrder[a.severity];
        return severityDiff !== 0 ? severityDiff : b.confidence - a.confidence;
      });

      setResults(sortedMatches);
    } catch (error) {
      console.error('Error checking plagiarism:', error);
      alert('An error occurred while checking for plagiarism: ' + error.message);
    }
    
    setIsLoading(false);
  };

  // UI Helper functions
  const getMatchIcon = (severity) => {
    switch (severity) {
      case 'high': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'medium': return <AlertTriangle className="w-5 h-5 text-orange-500" />;
      default: return <CheckCircle className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getMatchColor = (severity) => {
    switch (severity) {
      case 'high': return 'bg-red-50 border-red-200';
      case 'medium': return 'bg-orange-50 border-orange-200';
      default: return 'bg-yellow-50 border-yellow-200';
    }
  };

  const getSeverityText = (type, severity) => {
    if (type === 'exact') return 'Exact Copy Detected';
    if (type === 'consecutive') {
      switch (severity) {
        case 'high': return 'Significant Text Match';
        case 'medium': return 'Moderate Text Match';
        default: return 'Minor Text Match';
      }
    }
    return 'Similar Content Found';
  };

  // Calculate overall plagiarism score
  const calculatePlagiarismScore = () => {
    if (results.length === 0) return 0;
    
    const weights = { high: 1, medium: 0.6, low: 0.3 };
    const weightedSum = results.reduce((sum, match) => sum + (match.confidence * weights[match.severity]), 0);
    const totalWeight = results.reduce((sum, match) => sum + (100 * weights[match.severity]), 0);
    
    return Math.round((weightedSum / totalWeight) * 100) || 0;
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-white">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Professional Plagiarism Detection</h1>
            <p className="text-gray-600">Advanced text analysis against web sources using ScrapingBee API</p>
          </div>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="mb-8 p-6 bg-gray-50 border rounded-lg">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Configuration</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                ScrapingBee API Key *
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter your ScrapingBee API key"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Get your key at <a href="https://scrapingbee.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">scrapingbee.com</a>
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Match Length (words)
              </label>
              <input
                type="number"
                value={minMatchLength}
                onChange={(e) => setMinMatchLength(Math.max(3, parseInt(e.target.value) || 5))}
                min="3"
                max="20"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={caseSensitive}
                onChange={(e) => setCaseSensitive(e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">Case sensitive matching</span>
            </label>
          </div>
        </div>
      )}

      {/* URL Input Section */}
      <div className="mb-8 p-6 bg-gray-50 rounded-lg">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Globe className="w-5 h-5" />
          Source URLs
        </h2>
        <div className="space-y-3">
          {urls.map((url, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="url"
                value={url}
                onChange={(e) => updateUrl(index, e.target.value)}
                placeholder="https://example.com/article-or-document"
                className={`flex-1 px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 ${
                  url && !isValidUrl(url) ? 'border-red-300 bg-red-50' : 'border-gray-300'
                }`}
              />
              {urls.length > 1 && (
                <button
                  onClick={() => removeUrlField(index)}
                  className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          onClick={addUrlField}
          className="mt-3 flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Source URL
        </button>
      </div>

      {/* Text Input Section */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Trainee's Text to Analyze
        </h2>
        <textarea
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Paste the text from the trainee's work that you want to check for plagiarism. For best results, use at least 20 characters..."
          className="w-full h-48 px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical font-mono text-sm"
        />
        <div className="mt-2 flex justify-between text-sm text-gray-500">
          <span>Characters: {searchText.length}</span>
          <span>Words: {searchText.trim() ? searchText.trim().split(/\s+/).length : 0}</span>
        </div>
      </div>

      {/* Analysis Button */}
      <div className="mb-8">
        <button
          onClick={checkPlagiarism}
          disabled={isLoading}
          className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors text-lg font-semibold"
        >
          {isLoading ? (
            <Loader className="w-5 h-5 animate-spin" />
          ) : (
            <Search className="w-5 h-5" />
          )}
          {isLoading ? 'Analyzing Sources...' : 'Analyze for Plagiarism'}
        </button>
      </div>

      {/* Results Section */}
      {(results.length > 0 || (fetchedContent.length > 0 && !isLoading)) && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-800">Analysis Results</h2>
            {results.length > 0 && (
              <div className="text-right">
                <div className="text-2xl font-bold text-gray-900">{calculatePlagiarismScore()}%</div>
                <div className="text-sm text-gray-600">Similarity Score</div>
              </div>
            )}
          </div>

          {/* Source Summary */}
          <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {fetchedContent.map((source, index) => (
              <div key={index} className={`p-4 rounded-lg border ${source.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4" />
                  <span className="font-medium text-sm truncate" title={source.url}>
                    {new URL(source.url).hostname}
                  </span>
                </div>
                {source.error ? (
                  <p className="text-red-600 text-sm">{source.error}</p>
                ) : (
                  <p className="text-green-700 text-sm">{source.wordCount} words extracted</p>
                )}
              </div>
            ))}
          </div>
          
          {results.length === 0 ? (
            <div className="p-6 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-6 h-6 text-green-500" />
                <h3 className="font-semibold text-green-800">No Significant Matches Found</h3>
              </div>
              <p className="text-green-700 mt-2">
                The submitted text appears to be original or does not contain significant matches with the provided sources.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {results.map((match, index) => (
                <div key={index} className={`p-5 border rounded-lg ${getMatchColor(match.severity)}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getMatchIcon(match.severity)}
                      <h3 className="font-semibold text-gray-800">
                        {getSeverityText(match.type, match.severity)}
                      </h3>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-bold text-gray-800">{match.confidence}%</span>
                      <div className="text-xs text-gray-600 uppercase tracking-wide">{match.severity} Risk</div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-3 rounded border mb-3">
                    <p className="text-gray-700 font-mono text-sm">
                      "{match.text.substring(0, 200)}{match.text.length > 200 ? '...' : ''}"
                    </p>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600">
                      <strong>Source:</strong> {new URL(match.url).hostname}
                    </span>
                    {match.wordCount && (
                      <span className="text-gray-600">
                        {match.wordCount} words matched
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Production Notes */}
      <div className="mt-12 p-6 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-semibold text-blue-800 mb-3">Production Features:</h3>
        <ul className="space-y-2 text-blue-700">
          <li>• <strong>ScrapingBee API Integration:</strong> Professional web scraping with JavaScript rendering</li>
          <li>• <strong>Advanced Text Analysis:</strong> Exact matches, consecutive phrases, and similarity detection</li>
          <li>• <strong>Configurable Settings:</strong> Adjust sensitivity and matching parameters</li>
          <li>• <strong>Error Handling:</strong> Robust error management for failed requests</li>
          <li>• <strong>Smart Content Extraction:</strong> Focuses on main content, filters out navigation</li>
          <li>• <strong>Severity Scoring:</strong> Risk assessment with detailed confidence metrics</li>
        </ul>
        <div className="mt-4 p-3 bg-white rounded border">
          <p className="text-sm text-blue-800">
            <strong>Setup Required:</strong> Get a ScrapingBee API key at scrapingbee.com (free tier available with 1,000 requests/month)
          </p>
        </div>
      </div>
    </div>
  );
}